import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TimerContext = createContext(null);

export function TimerProvider({ children }) {
  const { user } = useAuth();
  const [timer, setTimer] = useState(null); // {project_id, task_id, started_at, paused_at, worked_seconds, is_paused}
  const [tick, setTick] = useState(0);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) { setTimer(null); return; }
    try {
      const r = await api.get("/timer/active");
      setTimer(r.data && r.data.id ? r.data : null);
    } catch (e) { console.warn("[TimerContext] refresh failed", e); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll every 10s; local seconds counter ticks every 1s for the chip UI
  useEffect(() => {
    if (!user) return;
    pollRef.current = setInterval(refresh, 10000);
    return () => clearInterval(pollRef.current);
  }, [user, refresh]);

  useEffect(() => {
    if (!timer || timer.is_paused) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [timer?.id, timer?.is_paused]);

  const start = async ({ project_id, task_id, description = "" }) => {
    try {
      const r = await api.post("/timer/start", { project_id, task_id, description });
      setTimer({ ...r.data, worked_seconds: 0, is_paused: false });
      toast.success("Timer started");
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start timer");
      throw e;
    }
  };

  const pause = async () => {
    try {
      const r = await api.post("/timer/pause");
      setTimer({ ...r.data, is_paused: true });
      toast.success("Timer paused");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resume = async () => {
    try {
      const r = await api.post("/timer/resume");
      setTimer({ ...r.data, is_paused: false });
      toast.success("Timer resumed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const stop = async () => {
    try {
      const r = await api.post("/timer/stop");
      setTimer(null);
      toast.success(`Logged ${r.data.hours}h`);
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      throw e;
    }
  };

  // Compute live worked seconds (server gives baseline; we add local time since last refresh if running)
  const liveWorkedSeconds = () => {
    if (!timer) return 0;
    if (timer.is_paused) return Math.round(timer.worked_seconds || 0);
    // Started fresh on this client — compute from started_at
    const started = new Date(timer.started_at);
    const pausedTotal = Number(timer.total_paused_seconds || 0);
    const now = new Date();
    return Math.max(0, Math.round((now - started) / 1000 - pausedTotal));
  };

  return (
    <TimerContext.Provider value={{ timer, refresh, start, pause, resume, stop, liveWorkedSeconds, tick }}>
      {children}
    </TimerContext.Provider>
  );
}

export const useTimer = () => useContext(TimerContext);
